package dev.lingvo.shared.screenshots.geom

import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.render.Affine
import kotlin.math.abs
import kotlin.math.round

/** Coordinates and guides use strip axes, independent of an individual layer's rotation. */
enum class Axis { X, Y }

/** This declaration order is the stable priority for targets at equal distance. */
enum class SnapKind { SAFE_EDGE, CANVAS_CENTER, CANVAS_EDGE, SEAM, LAYER_CENTER, LAYER_EDGE, GRID }

/** Source identity makes a tie deterministic even when layer traversal order changes. */
data class SnapLine(val pos: Double, val kind: SnapKind, val sourceId: String?)

/** Immutable per-gesture targets avoid reading mutable editor state while dragging. */
data class SnapTargets(
    val x: List<SnapLine>, val y: List<SnapLine>,
    val bounds: Rect = Rect(0.0, 0.0, 0.0, 0.0),
    val neighbours: List<Pair<String, Rect>> = emptyList(),
)

/** Compass handles identify which edges may move; other edges stay fixed. */
enum class Handle { N, NE, E, SE, S, SW, W, NW }

/** Deltas stay in strip coordinates; guides describe only the targets actually chosen. */
data class SnapResult(val delta: Point, val guides: List<Guide>)

/** Geometric guides remain accessible to both a canvas and a textual editor status. */
sealed interface Guide {
    /** A target line spans the strip on its perpendicular axis. */
    data class Line(val axis: Axis, val pos: Double, val from: Double, val to: Double) : Guide
    /** Ordered spans identify the gaps that became equal, rather than only their colour. */
    data class Spacing(val axis: Axis, val gap: Double, val spans: List<Pair<Double, Double>>) : Guide
}

/**
 * Builds per-slot targets; [safe] is supplied by the safe-area owner in single-slot coordinates.
 * Omitting it adds no safe-area targets. Selected groups and descendants, and selected children's ancestors,
 * cannot attract themselves. Hidden subtrees are excluded; visible locked layers remain useful alignment targets.
 */
fun snapTargets(page: Page, board: Board, exclude: Set<String>, gridColumns: Int?, safe: Rect? = null): SnapTargets {
    require(gridColumns == null || gridColumns in 1..64) { "Grid columns must be in 1..64" }
    val w = board.canvas.width.toDouble()
    val h = board.canvas.height.toDouble()
    require(w > 0 && h > 0) { "Canvas dimensions must be positive" }
    val xs = mutableListOf<SnapLine>()
    val ys = mutableListOf<SnapLine>()
    for (slot in page.slotIds.indices) {
        val offset = slot * w
        val id = "canvas:$slot"
        xs += listOf(SnapLine(offset, SnapKind.CANVAS_EDGE, id), SnapLine(offset + w, SnapKind.CANVAS_EDGE, id), SnapLine(offset + w / 2, SnapKind.CANVAS_CENTER, id))
        ys += listOf(SnapLine(0.0, SnapKind.CANVAS_EDGE, id), SnapLine(h, SnapKind.CANVAS_EDGE, id), SnapLine(h / 2, SnapKind.CANVAS_CENTER, id))
        if (slot > 0) xs += SnapLine(offset, SnapKind.SEAM, "seam:$slot")
        safe?.let {
            xs += listOf(SnapLine(offset + it.x, SnapKind.SAFE_EDGE, id), SnapLine(offset + it.right, SnapKind.SAFE_EDGE, id))
            ys += listOf(SnapLine(it.y, SnapKind.SAFE_EDGE, id), SnapLine(it.bottom, SnapKind.SAFE_EDGE, id))
        }
        if (gridColumns != null) for (column in 1 until gridColumns) xs += SnapLine(offset + column * w / gridColumns, SnapKind.GRID, id)
    }
    val neighbours = mutableListOf<Pair<String, Rect>>()
    fun hasExcluded(layer: Layer): Boolean = layer.base.id in exclude || layer is GroupLayer && layer.children.any { hasExcluded(it) }
    fun visit(layers: List<Layer>, parent: Affine) {
        for (layer in layers) {
            if (layer.base.hidden || layer.base.id in exclude) continue
            val world = parent * layer.base.box.toParent()
            if (!hasExcluded(layer)) {
                val r = aabb(layer.base.box, parent)
                val id = layer.base.id
                neighbours += id to r
                xs += anchors(r, Axis.X).mapIndexed { i, pos -> SnapLine(pos, if (i == 1) SnapKind.LAYER_CENTER else SnapKind.LAYER_EDGE, id) }
                ys += anchors(r, Axis.Y).mapIndexed { i, pos -> SnapLine(pos, if (i == 1) SnapKind.LAYER_CENTER else SnapKind.LAYER_EDGE, id) }
            }
            if (layer is GroupLayer) visit(layer.children, world)
        }
    }
    visit(page.layers, Affine.IDENTITY)
    return SnapTargets(xs.distinct(), ys.distinct(), Rect(0.0, 0.0, w * page.slotIds.size, h), neighbours)
}

/** Moves independent AABB anchors to the nearest target, with a bounded equal-spacing adjustment. */
fun snapMove(moving: Rect, delta: Point, t: SnapTargets, threshold: Double): SnapResult {
    checkThreshold(threshold)
    val proposed = moving.copy(x = moving.x + delta.x, y = moving.y + delta.y)
    var x = best(lineCandidates(proposed, Axis.X, t, threshold))
    var y = best(lineCandidates(proposed, Axis.Y, t, threshold))
    // A cyclic overlap dependency has no stable guide; retaining the requested move avoids jitter.
    val seen = mutableSetOf<Pair<Double, Double>>()
    repeat(8) {
        if (!seen.add((x?.shift ?: 0.0) to (y?.shift ?: 0.0))) return SnapResult(delta, emptyList())
        val nextX = chooseMove(proposed.copy(y = proposed.y + (y?.shift ?: 0.0)), Axis.X, t, threshold)
        val nextY = chooseMove(proposed.copy(x = proposed.x + (nextX?.shift ?: 0.0)), Axis.Y, t, threshold)
        if (same(x, nextX) && same(y, nextY)) return SnapResult(
            Point(delta.x + (nextX?.shift ?: 0.0), delta.y + (nextY?.shift ?: 0.0)), listOfNotNull(nextX?.guide, nextY?.guide))
        x = nextX
        y = nextY
    }
    return SnapResult(delta, emptyList())
}

/**
 * Returns handle displacement, snapping moving edges only. Aspect-locked corners use the larger relative
 * change as driver; a side handle drives its own axis and the bottom/right dependent edge follows unsnapped.
 */
fun snapResize(start: Rect, handle: Handle, delta: Point, t: SnapTargets, threshold: Double, keepAspect: Boolean): SnapResult {
    checkThreshold(threshold)
    require(start.w > 0 && start.h > 0) { "Resize requires a positive rectangle" }
    val west = handle in listOf(Handle.W, Handle.NW, Handle.SW)
    val east = handle in listOf(Handle.E, Handle.NE, Handle.SE)
    val north = handle in listOf(Handle.N, Handle.NW, Handle.NE)
    val south = handle in listOf(Handle.S, Handle.SW, Handle.SE)
    val horizontal = west || east
    val vertical = north || south
    var dx = if (horizontal) delta.x else 0.0
    var dy = if (vertical) delta.y else 0.0
    val driveX = horizontal && (!vertical || abs(dx / start.w) >= abs(dy / start.h))
    val x = if (horizontal && (!keepAspect || driveX)) edgeCandidate((if (west) start.x else start.right) + dx, Axis.X, t, threshold) else null
    val y = if (vertical && (!keepAspect || !driveX)) edgeCandidate((if (north) start.y else start.bottom) + dy, Axis.Y, t, threshold) else null
    dx += x?.shift ?: 0.0
    dy += y?.shift ?: 0.0
    dx = if (west) dx.coerceAtMost(start.w - .01) else dx.coerceAtLeast(.01 - start.w)
    dy = if (north) dy.coerceAtMost(start.h - .01) else dy.coerceAtLeast(.01 - start.h)
    if (keepAspect) {
        if (driveX) dy = (if (north) -1 else 1) * (if (west) -dx else dx) * start.h / start.w
        else dx = (if (west) -1 else 1) * (if (north) -dy else dy) * start.w / start.h
    }
    val xGuide = x?.guide?.takeIf { abs(dx - delta.x - x.shift) < 1e-9 }
    val yGuide = y?.guide?.takeIf { abs(dy - delta.y - y.shift) < 1e-9 }
    return SnapResult(Point(dx, dy), listOfNotNull(xGuide, yGuide))
}

/** Normalizes a turn before testing cardinal magnets or forced fifteen-degree increments. */
fun snapAngle(deg: Double, forced: Boolean): Double {
    require(deg.isFinite()) { "Angle must be finite" }
    val normalized = (deg % 360 + 360) % 360
    if (forced) return (round(normalized / 15) * 15) % 360
    val cardinal = (round(normalized / 90) * 90) % 360
    val distance = minOf(abs(normalized - cardinal), 360 - abs(normalized - cardinal))
    return if (distance <= 2) cardinal else normalized
}

private data class Candidate(val shift: Double, val kind: SnapKind, val source: String?, val guide: Guide)
private val candidateOrder = compareBy<Candidate> { abs(it.shift) }.thenBy { it.kind.ordinal }.thenBy { it.source.orEmpty() }.thenBy { it.shift }
private fun best(candidates: List<Candidate>): Candidate? = candidates.minWithOrNull(candidateOrder)
private fun same(a: Candidate?, b: Candidate?): Boolean = abs((a?.shift ?: 0.0) - (b?.shift ?: 0.0)) < 1e-9
private fun anchors(r: Rect, axis: Axis): List<Double> = if (axis == Axis.X) listOf(r.x, r.x + r.w / 2, r.right) else listOf(r.y, r.y + r.h / 2, r.bottom)
private fun start(r: Rect, axis: Axis): Double = if (axis == Axis.X) r.x else r.y
private fun end(r: Rect, axis: Axis): Double = if (axis == Axis.X) r.right else r.bottom
private fun cross(axis: Axis): Axis = if (axis == Axis.X) Axis.Y else Axis.X
private fun checkThreshold(t: Double) = require(t.isFinite() && t >= 0) { "Snap threshold must be finite and nonnegative" }

private fun lineCandidates(r: Rect, axis: Axis, t: SnapTargets, threshold: Double): List<Candidate> =
    anchors(r, axis).flatMap { anchor -> candidatesAt(anchor, axis, t, threshold) }
private fun edgeCandidate(pos: Double, axis: Axis, t: SnapTargets, threshold: Double): Candidate? = best(candidatesAt(pos, axis, t, threshold))
private fun candidatesAt(pos: Double, axis: Axis, t: SnapTargets, threshold: Double): List<Candidate> =
    (if (axis == Axis.X) t.x else t.y).mapNotNull { line ->
        val shift = line.pos - pos
        if (abs(shift) > threshold) null else Candidate(shift, line.kind, line.sourceId,
            Guide.Line(axis, line.pos, start(t.bounds, cross(axis)), end(t.bounds, cross(axis))))
    }
private fun chooseMove(r: Rect, axis: Axis, t: SnapTargets, threshold: Double): Candidate? =
    best(lineCandidates(r, axis, t, threshold) + spacingCandidates(r, axis, t, threshold))

private fun spacingCandidates(r: Rect, axis: Axis, t: SnapTargets, threshold: Double): List<Candidate> {
    val perpendicular = cross(axis)
    val neighbours = t.neighbours.filter { (_, n) -> start(n, perpendicular) < end(r, perpendicular) && end(n, perpendicular) > start(r, perpendicular) }
        .sortedWith(compareBy<Pair<String, Rect>> { start(it.second, axis) }.thenBy { it.first })
    val candidates = mutableListOf<Candidate>()
    val size = end(r, axis) - start(r, axis)
    fun before(pos: Double): Pair<String, Rect>? = neighbours.filter { end(it.second, axis) <= pos + 1e-9 }
        .maxWithOrNull(compareBy<Pair<String, Rect>> { end(it.second, axis) }.thenByDescending { it.first })
    fun after(pos: Double): Pair<String, Rect>? = neighbours.firstOrNull { start(it.second, axis) >= pos + size - 1e-9 }
    fun add(pos: Double, gap: Double, spans: List<Pair<Double, Double>>, source: String) {
        val raw = pos - start(r, axis)
        val shift = if (abs(raw) < 1e-9) 0.0 else raw
        if (abs(shift) <= threshold) candidates += Candidate(shift, SnapKind.LAYER_EDGE, source, Guide.Spacing(axis, gap, spans))
    }
    // Validate neighbours at the proposed target, not at the pre-snap position. Every chosen target then
    // remains available at distance zero, even when the move crosses a neighbour boundary.
    for (a in neighbours) for (b in neighbours) {
        val left = end(a.second, axis)
        val right = start(b.second, axis)
        val gap = (right - left - size) / 2
        val pos = left + gap
        if (gap < 0 || abs(pos - start(r, axis)) * 2 > threshold) continue
        if (before(pos)?.first == a.first && after(pos)?.first == b.first) {
            add(pos, gap, listOf(left to pos, pos + size to right), "${a.first}:${b.first}")
        }
    }
    for ((a, b) in neighbours.zipWithNext()) {
        val gap = start(b.second, axis) - end(a.second, axis)
        if (gap < 0) continue
        val reference = end(a.second, axis) to start(b.second, axis)
        for (n in neighbours) {
            val left = end(n.second, axis)
            val right = start(n.second, axis)
            val posBefore = left + gap
            val posAfter = right - gap - size
            if (abs(posBefore - start(r, axis)) <= threshold && before(posBefore)?.first == n.first) {
                add(posBefore, gap, listOf(reference, left to posBefore), "${a.first}:${b.first}:${n.first}")
            }
            if (abs(posAfter - start(r, axis)) <= threshold && after(posAfter)?.first == n.first) {
                add(posAfter, gap, listOf(reference, posAfter + size to right), "${a.first}:${b.first}:${n.first}")
            }
        }
    }
    return candidates
}
